/**
 * Content List Item Component
 * Displays a content item as a row in list view
 */

import type { ContentItem } from "../../types/content.types";
import { ContentType } from "../../types/content.types";
import {
  DocumentIcon,
  PhotoIcon,
  VideoCameraIcon,
  DocumentTextIcon,
  PaperClipIcon,
  EyeIcon,
  EyeSlashIcon,
  TrashIcon,
  PencilIcon,
  ShareIcon,
} from "@heroicons/react/24/outline";
import clsx from "clsx";

interface ContentListItemProps {
  item: ContentItem;
  selected: boolean;
  onSelect: (selected: boolean) => void;
  onClick: () => void;
  onDelete: () => void;
  onShare?: () => void;
}

export default function ContentListItem({
  item,
  selected,
  onSelect,
  onClick,
  onDelete,
  onShare,
}: ContentListItemProps) {
  const getTypeIcon = () => {
    const iconClass = "h-5 w-5";
    switch (item.content_type) {
      case ContentType.FLIPBOOK:
        return <DocumentIcon className={clsx(iconClass, "text-purple-600")} />;
      case ContentType.IMAGE:
        return <PhotoIcon className={clsx(iconClass, "text-green-600")} />;
      case ContentType.VIDEO_LINK:
        return <VideoCameraIcon className={clsx(iconClass, "text-red-600")} />;
      case ContentType.TEXT_BLOCK:
        return (
          <DocumentTextIcon className={clsx(iconClass, "text-blue-600")} />
        );
      case ContentType.FILE:
        return <PaperClipIcon className={clsx(iconClass, "text-gray-600")} />;
      default:
        return <DocumentIcon className={clsx(iconClass, "text-gray-600")} />;
    }
  };

  const getTypeLabel = () => {
    switch (item.content_type) {
      case ContentType.FLIPBOOK:
        return "Flipbook";
      case ContentType.IMAGE:
        return "Image";
      case ContentType.VIDEO_LINK:
        return "Video";
      case ContentType.TEXT_BLOCK:
        return "Text";
      case ContentType.FILE:
        return "File";
      default:
        return "Unknown";
    }
  };

  const getTypeColor = () => {
    switch (item.content_type) {
      case ContentType.FLIPBOOK:
        return "bg-purple-100 text-purple-700";
      case ContentType.IMAGE:
        return "bg-green-100 text-green-700";
      case ContentType.VIDEO_LINK:
        return "bg-red-100 text-red-700";
      case ContentType.TEXT_BLOCK:
        return "bg-blue-100 text-blue-700";
      case ContentType.FILE:
        return "bg-gray-100 text-gray-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  return (
    <div
      className={clsx(
        "flex items-center px-4 py-3 rounded-lg border transition-all",
        selected
          ? "border-blue-500 bg-blue-50"
          : "border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm",
      )}
    >
      {/* Checkbox */}
      <div className="w-8">
        <input
          type="checkbox"
          checked={selected}
          onChange={(e) => onSelect(e.target.checked)}
          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
      </div>

      {/* Title & Thumbnail */}
      <div
        className="flex-1 flex items-center gap-3 cursor-pointer"
        onClick={onClick}
      >
        {/* Thumbnail */}
        <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center overflow-hidden flex-shrink-0">
          {item.thumbnail_url ? (
            <img
              src={item.thumbnail_url}
              alt={item.title}
              className="w-full h-full object-cover"
            />
          ) : (
            getTypeIcon()
          )}
        </div>

        {/* Title & Description */}
        <div className="min-w-0">
          <h3 className="font-medium text-gray-900 truncate">
            {item.is_featured && (
              <svg
                className="inline h-4 w-4 text-yellow-500 mr-1 -mt-0.5"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            )}
            {item.title}
          </h3>
          {item.description && (
            <p className="text-sm text-gray-500 truncate">{item.description}</p>
          )}
        </div>
      </div>

      {/* Type */}
      <div className="w-24 text-center">
        <span
          className={clsx("px-2 py-1 text-xs rounded-full", getTypeColor())}
        >
          {getTypeLabel()}
        </span>
      </div>

      {/* Created By */}
      <div className="w-32 text-sm text-gray-600 truncate">
        {item.creator?.full_name || item.creator?.email?.split("@")[0] || "—"}
      </div>

      {/* Views */}
      <div className="w-24 text-center text-sm text-gray-600">
        {item.view_count || 0}
      </div>

      {/* Visibility */}
      <div className="w-24 text-center">
        {item.is_public ? (
          <span className="inline-flex items-center gap-1 text-sm text-green-600">
            <EyeIcon className="h-4 w-4" />
            Public
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-sm text-gray-500">
            <EyeSlashIcon className="h-4 w-4" />
            Private
          </span>
        )}
      </div>

      {/* Actions */}
      <div className="w-32 flex items-center justify-end gap-2">
        <button
          onClick={onClick}
          className="p-1.5 rounded-lg text-gray-500 hover:text-blue-600 hover:bg-blue-50 transition-colors"
          title="View Details"
        >
          <PencilIcon className="h-4 w-4" />
        </button>
        {onShare && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onShare();
            }}
            className="p-1.5 rounded-lg text-gray-500 hover:text-green-600 hover:bg-green-50 transition-colors"
            title="Share"
          >
            <ShareIcon className="h-4 w-4" />
          </button>
        )}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          className="p-1.5 rounded-lg text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors"
          title="Delete"
        >
          <TrashIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
